import { useState } from "react";
import { Box, BottomNavigation, BottomNavigationAction, Paper } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import type { SvgIconComponent } from "@mui/icons-material";
import AccountBalance from "@mui/icons-material/AccountBalance";
import SmartToy from "@mui/icons-material/SmartToy";
import SearchRounded from "@mui/icons-material/SearchRounded";
import PersonRounded from "@mui/icons-material/PersonRounded";
import { TopAppBar } from "./TopAppBar";
import { SearchMobileDialog } from "@/components/HeaderSearch";
import { IdleSentinel } from "@/components/IdleSentinel";
import { useCanAccessMarketplace } from "@/lib/features";

/**
 * V2 bottom-nav tab definitions.
 *
 * "nav" entries route to a path; the "search" entry opens an overlay
 * (fullscreen search sheet) instead of navigating — kept as its own
 * `kind` so MobileShellV2 knows to treat it specially. Ordering below
 * is the on-screen order from left to right.
 *
 * Marketplace used to live here but was moved (round 8) to a segmented
 * control at the top of the Bots page. Rationale: Marketplace is a peer
 * *view* of the Bots list rather than a top-level destination, and it
 * will eventually be gated by subscription tier — so the bottom nav
 * couldn't reserve a permanent slot for it anyway. The slot it vacated
 * is not backfilled; four tabs (Accounts, Bots, Search, Profile) is
 * deliberate.
 */
type V2Tab =
  | { kind: "nav"; label: string; path: string; icon: SvgIconComponent }
  | { kind: "search"; label: string; icon: SvgIconComponent };

const V2_TABS: V2Tab[] = [
  { kind: "nav", label: "Accounts", path: "/accounts", icon: AccountBalance },
  { kind: "nav", label: "Bots", path: "/bots", icon: SmartToy },
  { kind: "search", label: "Search", icon: SearchRounded },
  { kind: "nav", label: "Profile", path: "/profile", icon: PersonRounded }
];

/**
 * The set of peer "views" that live under the Bots bottom-nav tab.
 * Order here is also the re-tap cycle order: tapping the Bots icon
 * while already in one of these cycles to the next.
 *
 * Keeping this as an ordered tuple (rather than a Set) is the point —
 * we want `/bots → /marketplace → /testing → /lists → /bots` and we
 * want that sequence to live in exactly one place so both
 * `pathMatchesTab` and the re-tap handler agree. Lists lives here
 * because it's a bot *input* surface (symbol universes bots subscribe
 * to), not its own first-class destination.
 */
const BOTS_CYCLE: readonly string[] = [
  "/bots",
  "/marketplace",
  "/testing",
  "/lists"
];

/**
 * Which paths activate a given nav tab.
 *
 * The Bots tab lights up on every path in `BOTS_CYCLE` (currently
 * /bots, /marketplace, /testing, /lists) because those are peer views
 * of the bot workflow — a user who just tapped "Lists" from the
 * segmented control shouldn't see the Bots tab go dark under them.
 * Accounts is deliberately exact-match (was that way before this
 * change; preserving existing behavior).
 */
function pathMatchesTab(pathname: string, tabPath: string): boolean {
  if (tabPath === "/accounts") return pathname === "/accounts";
  if (tabPath === "/bots") {
    return BOTS_CYCLE.some(
      (p) => pathname === p || pathname.startsWith(p + "/")
    );
  }
  return pathname === tabPath || pathname.startsWith(tabPath + "/");
}

/**
 * Given the current URL, return the next Bots-family root to navigate
 * to when the user re-taps the Bots icon.
 *
 * Behaviour:
 *   • If we're on exactly a cycle root (e.g. `/marketplace`), advance
 *     to the next root (`/testing`).
 *   • If we're on a sub-route of a cycle member (e.g. `/bots/:id`),
 *     pop back to that root (`/bots`) — matches iOS "re-tap active
 *     tab returns to root" convention.
 *   • Otherwise (unexpected — we're on /bots family but not under a
 *     known root), fall back to `/bots`.
 */
function nextBotsCycleTarget(pathname: string): string {
  for (let i = 0; i < BOTS_CYCLE.length; i++) {
    const root = BOTS_CYCLE[i];
    if (pathname === root) return BOTS_CYCLE[(i + 1) % BOTS_CYCLE.length];
    if (pathname.startsWith(root + "/")) return root;
  }
  return BOTS_CYCLE[0];
}

/**
 * V2 mobile shell — Robinhood-inspired headerless layout.
 *
 * The only page that keeps the TopAppBar is `/profile`. Profile is where
 * the V1 header actions (Search, AI, LiveChat, Feedback, Notifications)
 * live as tappable rows in V2, so the bar there is still useful (it
 * carries the overflow menu + back nav semantics). Every other mobile
 * page renders with no top bar so the page content — especially the
 * Accounts page's chart — can use the full viewport height.
 *
 * Rationale for keeping Profile's header: the UI-version toggle is in
 * the overflow menu, so a V2 user needs at least one surface where the
 * bar is present — otherwise they could never toggle back to V1 without
 * manually clearing localStorage.
 *
 * Everything else matches MobileShell V1: bottom tab nav, safe-area
 * padding, sticky AppBar behaviour on pages that want one.
 */
export function MobileShellV2({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const canAccessMarketplace = useCanAccessMarketplace();

  const showTopBar = location.pathname === "/profile";

  // The BottomNavigation's `value` is a string key identifying the
  // active tab. For "nav" tabs it's the path; for "search" it's the
  // literal "search" — which only becomes active while the search
  // dialog is open (otherwise the underlying route keeps its tab lit).
  const currentTabValue = searchOpen
    ? "search"
    : (V2_TABS.find(
        (t): t is Extract<V2Tab, { kind: "nav" }> =>
          t.kind === "nav" && pathMatchesTab(location.pathname, t.path)
      )?.path ?? false);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Engagement tracker + idle-confirm modal. Renders null while
       *  the user is active or signed-out; sits at the shell root so
       *  one instance covers every page under the V2 shell. */}
      <IdleSentinel />
      {showTopBar && <TopAppBar />}
      <Box
        component="main"
        sx={{
          flex: 1,
          // No horizontal padding here so pages can own their own gutters.
          // (V1 uses px: 2 globally — V2 pages should render edge-to-edge
          // when they want to, and pad their inner content themselves.)
          pb: "calc(72px + env(safe-area-inset-bottom))",
          // No top padding when the bar is hidden — pages that want a
          // "pinned header" effect (like AccountsPageV2's sticky account
          // selector) need the content area to start at y=0.
          pt: showTopBar ? 0 : 0
        }}
      >
        {children}
      </Box>
      <Paper
        elevation={0}
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar
        }}
      >
        <BottomNavigation
          value={currentTabValue}
          onChange={(_, v) => {
            if (!v) return;
            if (v === "search") {
              setSearchOpen(true);
            } else {
              navigate(v);
            }
          }}
          showLabels
        >
          {V2_TABS.map((t) => {
            const Icon = t.icon;
            const key = t.kind === "nav" ? t.path : "search";
            return (
              <BottomNavigationAction
                key={key}
                value={key}
                label={t.label}
                icon={<Icon />}
                onClick={() => {
                  // MUI's BottomNavigation only fires onChange when the
                  // value *changes*, so re-taps on the already-active tab
                  // never reach onChange. We handle those here:
                  //
                  //   • Bots tab: cycles between the peer views
                  //     /bots → /marketplace → /testing → /bots. From a
                  //     sub-route like /bots/:id we pop back to that
                  //     cycle-member's root instead of advancing (matches
                  //     the iOS convention of re-tapping an active tab
                  //     returning to its root). When the user's tier
                  //     lacks Marketplace access, the cycle skips over it.
                  //   • Any other tab: re-tap just pops back to the tab's
                  //     root if we're currently on a sub-route.
                  //   • Search: re-tap is a no-op (the dialog is already
                  //     open; onChange handles opening on the first tap).
                  if (key !== currentTabValue) return;
                  if (t.kind === "search") return;
                  if (t.path === "/bots") {
                    let target = nextBotsCycleTarget(location.pathname);
                    // Skip /marketplace when entitlement is off. Avoid an
                    // infinite loop in the (impossible) case where every
                    // cycle member is gated — bail to /bots.
                    if (target === "/marketplace" && !canAccessMarketplace) {
                      target = nextBotsCycleTarget(target);
                      if (target === "/marketplace") target = "/bots";
                    }
                    if (target !== location.pathname) navigate(target);
                    return;
                  }
                  if (location.pathname !== t.path) navigate(t.path);
                }}
              />
            );
          })}
        </BottomNavigation>
      </Paper>
      {/* Fullscreen search sheet — opened via the Search tab. Mounted
       *  once at the shell level so it overlays the page and survives
       *  route changes without flicker. */}
      <SearchMobileDialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
      />
    </Box>
  );
}
